import React, { useState } from 'react';
import { Navigate, Outlet, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  LayoutDashboard,
  Send,
  FileCode,
  Users,
  List,
  BarChart3,
  Settings,
  LogOut,
  Menu,
  X,
  ShieldCheck,
  ChevronLeft
} from 'lucide-react';
import LogoMark from '../components/LogoMark';

export const DashboardLayout: React.FC = () => {
  const { isAuthenticated, isLoading, user, logout } = useAuth();
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex justify-center items-center">
        <div className="w-12 h-12 border-2 border-primary-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  const navItems = [
    { name: 'Dashboard', path: '/dashboard', icon: LayoutDashboard },
    { name: 'Campaigns', path: '/campaigns', icon: Send },
    { name: 'Templates', path: '/templates', icon: FileCode },
    { name: 'Recipients', path: '/recipients', icon: Users },
    { name: 'Recipient Lists', path: '/lists', icon: List },
    { name: 'Analytics Reports', path: '/analytics', icon: BarChart3 }
  ];

  // Admin-only navigation
  if (user?.role === 'ADMIN') {
    navItems.push({ name: 'System Settings', path: '/settings', icon: Settings });
  }

  const handleLinkClick = () => {
    setIsMobileMenuOpen(false);
  };

  return (
    <div className="min-h-screen flex bg-slate-50 dark:bg-[#070a13] text-slate-800 dark:text-slate-200 transition-colors duration-200">
      {/* Sidebar - Desktop */}
      <aside className={`hidden md:flex flex-col transition-all duration-300 ${isSidebarCollapsed ? 'w-20' : 'w-64'} glass-panel border-y-0 border-l-0 shrink-0 sticky top-0 h-screen overflow-hidden`}>
        {/* Brand */}
        <div className={`h-16 flex items-center border-b border-slate-200/50 dark:border-white/5 relative ${isSidebarCollapsed ? 'justify-center px-0' : 'justify-between px-6'}`}>
          <div className={`flex items-center gap-3 ${isSidebarCollapsed ? 'justify-center w-full' : ''}`}>
            {isSidebarCollapsed ? (
              <img
                src="/favicon/favicon-light-48.png"
                alt="Salty"
                className="w-9 h-9 shrink-0"
                style={{ filter: 'invert(38%) sepia(85%) saturate(600%) hue-rotate(345deg) brightness(95%) contrast(100%)' }}
              />
            ) : (
              <div className="flex items-center gap-2.5 min-w-0">
                <LogoMark variant="auto" height={44} className="shrink-0" />
                <div className="flex items-center gap-2 min-w-0">
                  <span className="text-slate-300 text-xl font-light select-none">|</span>
                  <span className="text-base font-semibold text-slate-600 tracking-wide truncate">ReachOut</span>
                </div>
              </div>
            )}
          </div>
          <button
            onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
            className={`text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200 p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-white/5 transition-colors shrink-0 ${
              isSidebarCollapsed ? 'hidden' : ''
            }`}
            title="Collapse Sidebar"
          >
            <ChevronLeft size={16} />
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-4 py-6 space-y-1 overflow-y-auto">
          {navItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname.startsWith(item.path);
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200 group font-medium text-sm border border-transparent ${
                  isActive
                    ? 'bg-primary-500/10 text-primary-500 border-primary-500/20'
                    : 'text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-white/5'
                } ${isSidebarCollapsed ? 'justify-center px-0' : ''}`}
                title={isSidebarCollapsed ? item.name : undefined}
              >
                <Icon size={18} className={isActive ? 'text-primary-500' : 'text-slate-500 dark:text-slate-400 group-hover:text-slate-800 dark:group-hover:text-slate-200'} />
                {!isSidebarCollapsed && <span>{item.name}</span>}
              </Link>
            );
          })}
        </nav>

        {/* User Card & Logout */}
        <div className={`p-4 border-t border-slate-200/50 dark:border-white/5 space-y-3 ${isSidebarCollapsed ? 'flex flex-col items-center px-2' : ''}`}>
          {isSidebarCollapsed ? (
            <button
              onClick={() => setIsSidebarCollapsed(false)}
              className="w-10 h-10 bg-primary-500 rounded-full flex justify-center items-center text-white font-bold uppercase shadow-lg shadow-primary-500/20 shrink-0 hover:opacity-90"
              title="Expand Sidebar"
            >
              {user?.name.charAt(0)}
            </button>
          ) : (
            <div className="flex items-center gap-3 px-2 py-1">
              <div className="w-10 h-10 bg-primary-500 rounded-full flex justify-center items-center text-white font-bold uppercase shadow-lg shadow-primary-500/20 shrink-0">
                {user?.name.charAt(0)}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold truncate text-slate-800 dark:text-white">{user?.name ? user.name.split(' ')[0] : ''}</div>
                <div className="text-xs text-slate-500 dark:text-slate-400 truncate flex items-center gap-1">
                  {user?.role === 'ADMIN' ? (
                    <>
                      <ShieldCheck size={12} className="text-accent-emerald inline" />
                      Admin
                    </>
                  ) : (
                    'User Account'
                  )}
                </div>
              </div>
            </div>
          )}
          
          <button
            onClick={() => logout()}
            className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-xl text-slate-500 dark:text-slate-400 hover:text-accent-rose hover:bg-accent-rose/5 border border-transparent hover:border-accent-rose/10 font-medium text-sm transition-all duration-200 ${
              isSidebarCollapsed ? 'justify-center px-0' : ''
            }`}
            title="Logout"
          >
            <LogOut size={16} />
            {!isSidebarCollapsed && <span>Logout</span>}
          </button>
          {!isSidebarCollapsed && (
            <div className="px-2 pt-1 flex items-center justify-between">
              <span className="text-[10px] text-slate-400 tracking-wide">salty | ReachOut</span>
              <span className="text-[10px] font-medium text-primary-500 bg-primary-500/10 border border-primary-500/20 rounded-full px-2 py-0.5 tracking-wide">
                v{process.env.REACT_APP_VERSION || '1.0.0'}
              </span>
            </div>
          )}
        </div>
      </aside>

      {/* Main Area */}
      <div className="flex-1 flex flex-col min-w-0 overflow-x-hidden relative">
        {/* Header - Mobile & Desktop Top Bar */}
        <header className="h-16 flex items-center justify-between px-6 border-b border-slate-200/50 dark:border-white/5 glass-panel border-x-0 relative z-20">
          <div className="flex items-center gap-3">
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="md:hidden text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200 p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-white/5"
            >
              {isMobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
            </button>
            {/* Collapse toggle on desktop */}
            <button
              onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
              className="hidden md:flex text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200 p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-white/5 transition-colors"
              title={isSidebarCollapsed ? 'Expand Sidebar' : 'Collapse Sidebar'}
            >
              <Menu size={20} />
            </button>
            <div className="flex items-center gap-2 md:hidden">
              <LogoMark variant="auto" height={26} className="shrink-0" />
              <span className="text-slate-300 dark:text-slate-600 font-light">|</span>
              <span className="text-sm font-semibold text-slate-600 dark:text-slate-300 tracking-wide">ReachOut</span>
            </div>
          </div>

          <div className="flex items-center gap-4">
            <div className="text-xs font-semibold text-slate-500 bg-slate-200/50 border border-slate-200/80 rounded-full px-3 py-1 flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-accent-emerald animate-pulse"></span>
              SES Service: Connected
            </div>
          </div>

        </header>

        {/* Mobile Navigation Drawer */}
        {isMobileMenuOpen && (
          <div className="md:hidden fixed inset-0 z-30 flex">
            {/* Backdrop */}
            <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setIsMobileMenuOpen(false)}></div>
            
            {/* Drawer */}
            <aside className="relative flex flex-col w-64 max-w-xs bg-white dark:bg-slate-950 border-r border-slate-200 dark:border-white/5 h-full z-40 p-4">
              <div className="flex items-center justify-between pb-6 mb-6 border-b border-slate-200 dark:border-white/5">
                <div className="flex items-center gap-2">
                  <LogoMark variant="auto" height={20} />
                  <span className="font-bold text-lg tracking-wide bg-gradient-to-r from-slate-900 to-slate-500 dark:from-white dark:to-slate-400 bg-clip-text text-transparent">ReachOut</span>
                </div>
                <button
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200 p-1 rounded-lg hover:bg-slate-100 dark:hover:bg-white/5"
                >
                  <X size={20} />
                </button>
              </div>

              <nav className="flex-1 space-y-1">
                {navItems.map((item) => {
                  const Icon = item.icon;
                  const isActive = location.pathname.startsWith(item.path);
                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      onClick={handleLinkClick}
                      className={`flex items-center gap-3 px-4 py-3 rounded-xl font-medium text-sm transition-all duration-200 ${
                        isActive
                          ? 'bg-primary-500/10 text-primary-500 border border-primary-500/20'
                          : 'text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-white/5 border border-transparent'
                      }`}
                    >
                      <Icon size={18} />
                      {item.name}
                    </Link>
                  );
                })}
              </nav>

              <div className="pt-4 border-t border-slate-200 dark:border-white/5 space-y-3">
                <div className="text-sm font-semibold text-slate-800 dark:text-white px-2">{user?.name ? user.name.split(' ')[0] : ''}</div>
                <button
                  onClick={() => {
                    handleLinkClick();
                    logout();
                  }}
                  className="w-full flex items-center gap-3 px-4 py-2.5 rounded-xl text-slate-500 dark:text-slate-400 hover:text-accent-rose hover:bg-accent-rose/5 border border-transparent hover:border-accent-rose/10 font-medium text-sm transition-all duration-200"
                >
                  <LogOut size={16} />
                  Logout
                </button>
              </div>
            </aside>
          </div>
        )}

        {/* Page Content Viewport */}
        <main className="flex-1 p-6 md:p-8 overflow-y-auto relative z-10">
          <Outlet />
        </main>
      </div>
    </div>
  );
};
